<template>
<modal-dialog
	:modal-visible="visible"
	:title="title"
	:confirm-button-text="confirmButtonText ? confirmButtonText : 'Add'"
	v-on:confirm="confirmAdd"
	v-on:closeModal="$emit('closeModal')">

	<form-group label="Type" col-class="col-md-4">

		<equipment-type-picker :value="payload"
			:no-text-field="!allowManualType"
			v-on:input="setEquipment">
		</equipment-type-picker>
		
	</form-group>

	<field label="Rate Unit"
		col-class="col-md-4"
		:params="{
			type:'select',
			options: [
				{text: 'Hourly',value:'hour'},
				{text: 'Daily',value:'day'},
				{text: 'Weekly',value:'week'},
				{text: 'Monthly',value:'month'}
			]
		}"
		v-model="payload.unit"
		v-on:change="rateUnitChange">
	</field>

	<field :label="'Rate ($/' + payload.unit + ')'" 
		col-class="col-md-4" 
		:params="{type:'number',min:0,step:0.01}" 
		v-model="payload.rate">
	</field>

	<field :label="qtyLabel" 	
		col-class="col-md-4" 
		:params="{type:'number',min:0,step:0.25}" 
		v-model="payload.qty">
	</field>

	<slot></slot>

</modal-dialog>
</template>
<script type="text/javascript">

import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"
import FormGroup from "@/components/FormGroup"

import EquipmentTypePicker from "@/components/super_components/EquipmentTypePicker"

export default {
	name: "EquipmentTypePickerModal",
	components: {
		ModalDialog,
		Field,
		FormGroup,
		EquipmentTypePicker
	},
	props: [
		"visible",
		"title",
		"record",
		"allowManualType",
		"confirmButtonText"
	],
	data() {
		return {
			payload: {
				typeName: null,
				rate: null,
				hrs: null,
				unit: null,
				equipment_type_id: null,
				multiRates: {}
			},
			equipmentTypes: [],
			manualSelection: false
		}
	},
	watch: {
		visible() {
			if (this.visible) {
				this.fetchData()

				if (this.isRecordEmpty(this.record)) {
					this.resetPayload()
				} else {
					this.setEquipment(this.record)
				}
			}
		},
		record() {
			if (this.record && !this.isRecordEmpty(this.record)) {
				this.setEquipment(this.record)
			}
		}
	},
	computed: {
		qtyLabel() {
			if (this.payload.unit)
				return this.payload.unit.charAt(0).toUpperCase() + this.payload.unit.slice(1) + "s"

			return "Hours"
		}
	},
	methods: {
		isRecordEmpty(record) {
			if (!record || typeof record !== "object") return true
			const hasType = record.type_id || record.equipment_type_id || record.type_name || record.typeName
			const hasRateUnit = record.charge_out_rate_unit
			return !hasType && !hasRateUnit
		},
		resetPayload() {
			this.payload = {
				typeName: null,
				type_id: null,
				rate: null,
				hrs: null,
				unit: null,
				equipment_type_id: null,
				multiRates: {}
			}
		},
		setEquipment(eqObj) {	
			// Don't process if eqObj is empty or invalid
			// Allow processing if it has type_id (equipment selected) or type_name, or if it has charge_out_rate_unit
			if (!eqObj || (!eqObj.type_name && !eqObj.type_id && !eqObj.charge_out_rate_unit)) {
				return
			}

			this.payload.typeName = eqObj.type_name
			
			this.payload.equipment_type_id = eqObj.type_id

			if (eqObj.qty !== undefined)
				this.payload.qty = eqObj.qty

			let rate = ""

			// Determine the correct unit from equipment type
			// When equipment type is selected (has type_id), ALWAYS set unit from equipment type data
			let unitFromEquipment = null
			
			// Handle multiRates (new system with multiple rate types)
			if ("multiRates" in eqObj && eqObj.multiRates && Object.keys(eqObj.multiRates).length > 0) {

				//find and set the first active rate
				let activeRateKey = Object.keys(eqObj.multiRates).find(key => eqObj.multiRates[key].enabled)

				if (activeRateKey) {
					if (+this.payload.rate == 0)
						rate = eqObj.multiRates[activeRateKey].rate
					else
						rate = this.payload.rate

					// Normalize the active rate key
					let normalizedKey = activeRateKey
					if (normalizedKey == 'hrs' || normalizedKey == 'hours')
						normalizedKey = "hour"
					else if (normalizedKey == 'days')
						normalizedKey = "day"
					unitFromEquipment = normalizedKey
				}
				else {
					// No active rate in multiRates, fall back to single rate
					rate = eqObj.rate
				}

				this.payload.multiRates = eqObj.multiRates
			}
			// Handle single rate (legacy system)
			else {
				if ("rate" in eqObj)
					rate = eqObj.rate
			}

			// When equipment type is selected, ALWAYS override unit from equipment type data
			// Prioritize in this order:
			// 1. Unit from multiRates active rate (if set above)
			// 2. charge_out_rate_unit from equipment type (from eqObj or fetched list)
			// 3. Normalized unit from eqObj
			if (eqObj.type_id) {
				// Equipment type is selected, determine unit from equipment type
				if (unitFromEquipment) {
					this.payload.unit = unitFromEquipment
				}
				else {
					// Try to get charge_out_rate_unit from eqObj first
					let chargeOutRateUnit = eqObj.charge_out_rate_unit
					
					// If not in eqObj, look it up from the fetched equipment types list
					if (!chargeOutRateUnit && this.equipmentTypes.length > 0) {
						let equipmentType = this.equipmentTypes.find(et => et.id == eqObj.type_id)
						if (equipmentType && equipmentType.charge_out_rate_unit) {
							chargeOutRateUnit = equipmentType.charge_out_rate_unit
						}
					}
					
					if (chargeOutRateUnit) {
						// Equipment type has a specific unit, use it
						if (chargeOutRateUnit == 'days') {
							this.payload.unit = "day"
						}
						else if (chargeOutRateUnit == 'hrs') {
							this.payload.unit = "hour"
						}
					}
					else if (eqObj.unit) {
						// Use normalized unit from eqObj if available
						this.payload.unit = eqObj.unit
					}
				}
			}
			// If no equipment type selected, only set unit if not already set
			else if (!this.payload.unit) {
				if (unitFromEquipment) {
					this.payload.unit = unitFromEquipment
				}
				else if (eqObj.unit) {
					this.payload.unit = eqObj.unit
				}
			}

			// Normalize unit values
			if (this.payload.unit == 'hrs' || this.payload.unit == 'hours') {
				this.payload.unit = "hour"
			}
			else if (this.payload.unit == 'days') {
				this.payload.unit = "day"
			}
			// Only default to "hour" if no unit information is available at all
			else if (!this.payload.unit) {
				this.payload.unit = "hour"
			}

			// Debug log to verify unit is set correctly
			console.log("Equipment unit set to:", this.payload.unit, "from charge_out_rate_unit:", eqObj.charge_out_rate_unit, "eqObj.unit:", eqObj.unit, "type_id:", eqObj.type_id)

			if (rate && typeof rate == 'string')
				rate = rate.replace(",","")
			
			rate = +rate
			rate = rate.toFixed(2)

			this.payload.rate = rate

			if (eqObj && "rate" in eqObj && !this.payload.rate)
				this.payload.rate = eqObj.rate
		},
		confirmAdd() {
			this.$emit("confirmAdd",this.payload)
		},
		rateUnitChange(unit) {
			//populate the rate

			if ("multiRates" in this.payload && this.payload.multiRates && unit in this.payload.multiRates) {
				let selectedRate = this.payload.multiRates[unit]

				if (selectedRate && selectedRate.enabled) {
					let selectedRateValue = +selectedRate.rate

					this.payload.rate = selectedRateValue.toFixed(2)
				}
				else
					this.payload.rate = 0
			}

			else if ("charge_out_rates_json" in this.payload && this.payload.charge_out_rates_json) {
				try {

					let multiRates = JSON.parse(this.payload.charge_out_rates_json)

					let rate = 0
					if (unit in multiRates) {
						rate = +multiRates[unit].rate

						this.payload.rate = rate.toFixed(2)
					}
				}
				catch (e) {

				}
			}

			this.payload.qty_unit = unit
		},
		fetchData() {
			let vm = this

			let promises = [
				this.getJSON("EquipmentType/get")
			]

			Promise.all(promises).then(responses => {
				if (responses[0].success) {
					vm.equipmentTypes = responses[0].data
				}
			})
		}
	},
	created() {
		if (this.visible) {
			this.fetchData()
			this.setEquipment(this.record)
		}
	}
}
</script>